import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Instagram, Music2, Youtube, Mail, Save, X, Building2, ExternalLink, ClipboardList } from 'lucide-react';
import { useDeals } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { fmtMoney, fmtDate } from '../lib/format';
import { dealTalentIds } from '../lib/deals';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import TalentPitchDashboard from '../components/TalentPitchDashboard';
import InitialsAvatar from '../components/InitialsAvatar';
import RateCardTable from '../components/RateCardTable';

const TalentProfile = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { talent, talentMap, deals, updateTalent, brandMap, stages } = useDeals();
  const stageMap = useMemo(() => Object.fromEntries(stages.map((s) => [s.id, s])), [stages]);
  const t = talentMap[id] || talent.find((x) => x.user_id === id);

  const canEdit = ['manager', 'talent_manager'].includes(user?.role) || user?.user_id === id;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  useEffect(() => { if (t) setForm(t); }, [t]);

  if (!t) return (
    <div className="p-8">
      <Link to="/talent" className="text-sm text-slate-500 hover:text-slate-800">← back</Link>
      <p className="mt-4 text-slate-500">talent not found.</p>
    </div>
  );

  const own = deals.filter((d) => dealTalentIds(d).includes(id));
  const PIPELINE_STAGES = new Set(['rate_confirmed', 'contracted', 'complete']);
  const pipeline = own.filter((d) => PIPELINE_STAGES.has(d.stage)).reduce((s, d) => s + (Number(d.value) || 0), 0);
  const confirmed = own.filter((d) => ['contracted', 'complete'].includes(d.stage)).reduce((s, d) => s + (Number(d.value) || 0), 0);
  const paid = own.filter((d) => d.paid_out_at).reduce((s, d) => s + (Number(d.value) || 0), 0);

  const save = async () => {
    const patch = {
      name: form.name, handle: form.handle, bio: form.bio,
      instagram: form.instagram, tiktok: form.tiktok, youtube: form.youtube,
      rate_card: form.rate_card,
      rate_card_items: form.rate_card_items || {},
      insights_link: form.insights_link || '',
      title: form.title || '',
      commission_percent: form.commission_percent === '' || form.commission_percent == null ? null : Number(form.commission_percent),
      vat_registered: Boolean(form.vat_registered),
      legal_company_name: form.legal_company_name || '',
      company_number: form.company_number || '',
      address: form.address || '',
      vat_number: form.vat_number || '',
      personal_email: form.personal_email || '',
      shipping_address: form.shipping_address || '',
      dietaries: form.dietaries || '',
      clothing_size: form.clothing_size || '',
      shoe_size: form.shoe_size || '',
      phone_number: form.phone_number || '',
      insights: form.insights || '',
    };
    await updateTalent(id, patch);
    setEditing(false);
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center gap-2 mb-6">
        {['manager', 'talent_manager'].includes(user?.role) && (
          <button onClick={() => nav('/talent')} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
            <ArrowLeft className="w-4 h-4" /> talent
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <aside className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex flex-col items-center text-center">
            <InitialsAvatar name={t.name} size={96} className="ring-4 ring-lilac-100 text-3xl" />
            {!editing ? (
              <>
                <h2 className="mt-3 text-xl font-semibold text-slate-900">{t.name}</h2>
                <p className="text-sm text-slate-500">{t.title || t.handle || t.email}</p>
                {t.niche && <span className="mt-2 inline-block text-xs px-2.5 py-1 rounded-full bg-lilac-100 text-lilac-700">{t.niche}</span>}
                <p className="mt-3 text-xs text-slate-500 max-w-xs">{t.bio || 'No bio yet.'}</p>

                {(t.shipping_address || t.dietaries || t.clothing_size || t.shoe_size || t.phone_number || t.insights) && (
                  <div className="mt-4 w-full text-left text-xs text-slate-600 space-y-1.5">
                    {t.shipping_address && <div><span className="text-[10px] uppercase tracking-widest text-slate-400">shipping</span><div className="whitespace-pre-wrap">{t.shipping_address}</div></div>}
                    <div className="grid grid-cols-2 gap-2">
                      {t.clothing_size && <div><span className="text-[10px] uppercase tracking-widest text-slate-400">clothing</span><div>{t.clothing_size}</div></div>}
                      {t.shoe_size && <div><span className="text-[10px] uppercase tracking-widest text-slate-400">shoe</span><div>{t.shoe_size}</div></div>}
                    </div>
                    {t.dietaries && <div><span className="text-[10px] uppercase tracking-widest text-slate-400">dietaries</span><div>{t.dietaries}</div></div>}
                    {t.phone_number && <div><span className="text-[10px] uppercase tracking-widest text-slate-400">phone</span><div>{t.phone_number}</div></div>}
                    {t.insights && <div><span className="text-[10px] uppercase tracking-widest text-slate-400">insights</span><div className="whitespace-pre-wrap">{t.insights}</div></div>}
                  </div>
                )}
              </>
            ) : (
              <div className="w-full mt-3 space-y-2">
                <Input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" />
                <Input value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title (e.g. Founder, Head of Talent)" data-testid="talent-title-input" />
                <Input value={form.handle || ''} onChange={(e) => setForm({ ...form, handle: e.target.value })} placeholder="@handle" />
                <Textarea value={form.bio || ''} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} placeholder="Bio" />
                <Textarea value={form.shipping_address || ''} onChange={(e) => setForm({ ...form, shipping_address: e.target.value })} rows={2} placeholder="Shipping address" data-testid="talent-shipping-input" />
                <Input value={form.dietaries || ''} onChange={(e) => setForm({ ...form, dietaries: e.target.value })} placeholder="Dietaries (e.g. vegetarian, gluten-free)" data-testid="talent-dietaries-input" />
                <div className="grid grid-cols-2 gap-2">
                  <Input value={form.clothing_size || ''} onChange={(e) => setForm({ ...form, clothing_size: e.target.value })} placeholder="Clothing size" data-testid="talent-clothing-input" />
                  <Input value={form.shoe_size || ''} onChange={(e) => setForm({ ...form, shoe_size: e.target.value })} placeholder="Shoe size" data-testid="talent-shoe-input" />
                </div>
                <Input value={form.phone_number || ''} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} placeholder="Phone number" data-testid="talent-phone-input" />
                <Textarea value={form.insights || ''} onChange={(e) => setForm({ ...form, insights: e.target.value })} rows={3} placeholder="Insights (audience data, engagement highlights, key notes)" data-testid="talent-insights-input" />
              </div>
            )}
          </div>

          <div className="mt-6 space-y-2 text-sm">
            <div className="flex items-center gap-2 text-slate-600"><Mail className="w-4 h-4 text-slate-400" />{t.email}</div>
            {!editing ? (
              <>
                {t.commission_percent != null && (
                  <div className="text-xs text-slate-500">Default commission: <span className="font-medium text-slate-700">{t.commission_percent}%</span></div>
                )}
                {t.instagram && <div className="flex items-center gap-2 text-slate-600"><Instagram className="w-4 h-4 text-slate-400" /><a href={t.instagram} target="_blank" rel="noreferrer" className="hover:text-mint-700 truncate">{t.instagram}</a></div>}
                {t.tiktok && <div className="flex items-center gap-2 text-slate-600"><Music2 className="w-4 h-4 text-slate-400" /><a href={t.tiktok} target="_blank" rel="noreferrer" className="hover:text-mint-700 truncate">{t.tiktok}</a></div>}
                {t.youtube && <div className="flex items-center gap-2 text-slate-600"><Youtube className="w-4 h-4 text-slate-400" /><a href={t.youtube} target="_blank" rel="noreferrer" className="hover:text-mint-700 truncate">{t.youtube}</a></div>}
              </>
            ) : (
              <>
                <Input value={form.instagram || ''} onChange={(e) => setForm({ ...form, instagram: e.target.value })} placeholder="Instagram URL" />
                <Input value={form.tiktok || ''} onChange={(e) => setForm({ ...form, tiktok: e.target.value })} placeholder="TikTok URL" />
                <Input value={form.youtube || ''} onChange={(e) => setForm({ ...form, youtube: e.target.value })} placeholder="YouTube URL" />
                <div>
                  <label className="text-xs text-slate-500">Default commission %</label>
                  <Input type="number" min="0" max="100" value={form.commission_percent ?? ''} onChange={(e) => setForm({ ...form, commission_percent: e.target.value })} placeholder="e.g. 20" />
                </div>
              </>
            )}
          </div>

          {canEdit && (
            <div className="mt-6 flex gap-2">
              {editing ? (
                <>
                  <Button onClick={save} className="flex-1 bg-mint-500 hover:bg-mint-600 text-slate-900 font-semibold"><Save className="w-4 h-4 mr-1.5" /> save</Button>
                  <Button variant="ghost" onClick={() => { setForm(t); setEditing(false); }}><X className="w-4 h-4" /></Button>
                </>
              ) : (
                <Button variant="ghost" onClick={() => setEditing(true)} className="w-full text-mint-700 hover:bg-mint-50">edit profile</Button>
              )}
            </div>
          )}
        </aside>

        <div className="lg:col-span-2 space-y-5">
          <TalentPitchDashboard talentId={id} />

          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-[11px] uppercase tracking-wide text-slate-400">pipeline</div><div className="text-2xl font-semibold text-slate-900 mt-1">{fmtMoney(pipeline)}</div></div>
            <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-[11px] uppercase tracking-wide text-slate-400">confirmed</div><div className="text-2xl font-semibold text-mint-700 mt-1">{fmtMoney(confirmed)}</div></div>
            <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-[11px] uppercase tracking-wide text-slate-400">paid</div><div className="text-2xl font-semibold text-emerald-600 mt-1">{fmtMoney(paid)}</div></div>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100"><h3 className="text-sm font-semibold text-slate-700">deals</h3></div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr><th className="text-left font-medium px-4 py-2">deal</th><th className="text-left font-medium px-4 py-2">brand</th><th className="text-left font-medium px-4 py-2">stage</th><th className="text-left font-medium px-4 py-2">due</th><th className="text-right font-medium px-4 py-2">value</th></tr>
              </thead>
              <tbody>
                {own.map((d) => (
                  <tr key={d.id} className="border-t border-slate-100">
                    <td className="px-4 py-2 text-slate-800">{d.title}<div className="text-xs text-slate-500">{d.type}</div></td>
                    <td className="px-4 py-2 text-slate-700">{brandMap[d.brand_id]?.logo} {brandMap[d.brand_id]?.name}</td>
                    <td className="px-4 py-2"><span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] bg-slate-100 text-slate-700"><span className="w-1.5 h-1.5 rounded-full" style={{ background: stageMap[d.stage]?.color }} />{stageMap[d.stage]?.label}</span></td>
                    <td className="px-4 py-2 text-slate-600">{fmtDate(d.due_date)}</td>
                    <td className="px-4 py-2 text-right font-medium text-slate-900">{fmtMoney(d.value, d.currency)}</td>
                  </tr>
                ))}
                {own.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400 text-sm">no deals yet</td></tr>}
              </tbody>
            </table>
          </div>

          {/* Rate card & insights */}
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="rate-card-section">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-slate-400" />
              <h3 className="text-sm font-semibold text-slate-700">rate card & insights</h3>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">insights link</div>
                {!editing ? (
                  t.insights_link ? (
                    <a
                      href={t.insights_link}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-pink-600 hover:text-pink-700 hover:underline"
                      data-testid="insights-link-view"
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> open insights
                    </a>
                  ) : (
                    <span className="text-sm italic text-slate-400">not set</span>
                  )
                ) : (
                  <Input
                    value={form.insights_link || ''}
                    onChange={(e) => setForm({ ...form, insights_link: e.target.value })}
                    placeholder="https://…"
                    className="mt-1"
                    data-testid="insights-link-input"
                  />
                )}
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">rate card</div>
                <RateCardTable
                  values={editing ? (form.rate_card_items || {}) : (t.rate_card_items || {})}
                  editable={editing}
                  talentName={t.name}
                  onChange={(itemId, value) => setForm({ ...form, rate_card_items: { ...(form.rate_card_items || {}), [itemId]: value } })}
                />
              </div>
            </div>
          </div>

          {/* Business & legal details */}
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="business-details-section">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-slate-400" />
              <h3 className="text-sm font-semibold text-slate-700">business & legal details</h3>
              {t.vat_registered && !editing && (
                <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-pink-100 text-pink-700 font-medium">VAT registered</span>
              )}
            </div>

            {!editing ? (
              <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-0.5">legal company name</div>
                  <div className="text-slate-800">{t.legal_company_name || <span className="italic text-slate-400">not set</span>}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-0.5">company number</div>
                  <div className="text-slate-800">{t.company_number || <span className="italic text-slate-400">not set</span>}</div>
                </div>
                <div className="md:col-span-2">
                  <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-0.5">address</div>
                  <div className="text-slate-800 whitespace-pre-line">{t.address || <span className="italic text-slate-400">not set</span>}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-0.5">VAT number</div>
                  <div className="text-slate-800">
                    {t.vat_registered
                      ? (t.vat_number || <span className="italic text-slate-400">not set</span>)
                      : <span className="italic text-slate-400">not VAT registered</span>}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-0.5">personal email</div>
                  <div className="text-slate-800">{t.personal_email || <span className="italic text-slate-400">not set</span>}</div>
                </div>
              </div>
            ) : (
              <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={Boolean(form.vat_registered)}
                      onCheckedChange={(v) => setForm({ ...form, vat_registered: Boolean(v) })}
                      data-testid="vat-registered-checkbox"
                    />
                    <span className="text-sm text-slate-700">VAT registered</span>
                    <span className="text-xs text-slate-400">— tick if they charge VAT on invoices</span>
                  </label>
                </div>
                <div>
                  <Label>Legal company name</Label>
                  <Input value={form.legal_company_name || ''} onChange={(e) => setForm({ ...form, legal_company_name: e.target.value })} placeholder="e.g. Ava Mercer Ltd" className="mt-1" data-testid="legal-company-name-input" />
                </div>
                <div>
                  <Label>Company number</Label>
                  <Input value={form.company_number || ''} onChange={(e) => setForm({ ...form, company_number: e.target.value })} placeholder="e.g. 12345678" className="mt-1" data-testid="company-number-input" />
                </div>
                <div className="md:col-span-2">
                  <Label>Registered address</Label>
                  <Textarea value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={3} placeholder={`123 Example Street\nLondon\nUK`} className="mt-1" data-testid="address-input" />
                </div>
                {form.vat_registered && (
                  <div>
                    <Label>VAT number</Label>
                    <Input value={form.vat_number || ''} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} placeholder="e.g. GB123456789" className="mt-1" data-testid="vat-number-input" />
                  </div>
                )}
                <div className={form.vat_registered ? '' : 'md:col-span-2'}>
                  <Label>Personal email</Label>
                  <Input type="email" value={form.personal_email || ''} onChange={(e) => setForm({ ...form, personal_email: e.target.value })} placeholder="ava@personal.com" className="mt-1" data-testid="personal-email-input" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TalentProfile;
